import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { ClipboardList, CheckCircle2 } from 'lucide-react';

/**
 * TarefasLeadPanel — exibe as tarefas pendentes de um lead.
 *
 * Props:
 *   leadId  (number)  — ID do lead
 *   onCriarTarefa  () => void  — abre o modal de criar tarefa
 */
export default function TarefasLeadPanel({ leadId, onCriarTarefa }) {
  const [tarefas, setTarefas]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [marcando, setMarcando] = useState(null); // id da tarefa em andamento
  const [expandido, setExpandido] = useState(true);

  const carregar = useCallback(() => {
    if (!leadId) return;
    setLoading(true);
    api.get(`/agenda/tarefas-cliente/?lead_id=${leadId}`)
      .then(r => setTarefas(r.data.filter(t => !t.concluido)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => { carregar(); }, [carregar]);

  const marcarConcluida = async (id) => {
    setMarcando(id);
    const agora = new Date().toISOString();
    try {
      await api.patch(`/agenda/${id}/`, { concluido: true, concluido_em: agora });
      setTarefas(ts => ts.filter(t => t.id !== id));
    } catch (e) {
      alert('Erro ao concluir tarefa: ' + (e.response?.data?.error || e.message || 'tente novamente'));
    }
    setMarcando(null);
  };

  const fmtDT = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  const S = {
    wrapper: {
      background: '#0a0f1a',
      border: '1px solid #1a2035',
      borderRadius: 10,
      overflow: 'hidden',
      marginBottom: 0,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 14px',
      background: '#0e1420',
      borderBottom: expandido ? '1px solid #1a2035' : 'none',
      cursor: 'pointer',
      userSelect: 'none',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: 7,
      fontSize: 12,
      fontWeight: 700,
      color: '#94a3b8',
      textTransform: 'uppercase',
      letterSpacing: 1,
    },
    badge: {
      background: 'rgba(251,146,60,0.15)',
      color: '#fb923c',
      borderRadius: 10,
      padding: '1px 7px',
      fontSize: 11,
      fontWeight: 700,
    },
    body: {
      padding: '10px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    item: {
      background: '#111827',
      border: '1px solid #1a2035',
      borderRadius: 8,
      padding: '10px 12px',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 10,
    },
    itemLeft: {
      flex: 1,
      minWidth: 0,
    },
    titulo: {
      fontSize: 13,
      color: '#e2e8f0',
      fontWeight: 600,
      marginBottom: 3,
      wordBreak: 'break-word',
    },
    meta: {
      fontSize: 11,
      color: '#475569',
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap',
    },
    btnConcluir: (dis) => ({
      padding: '6px 12px',
      borderRadius: 7,
      border: 'none',
      background: dis ? 'rgba(74,222,128,0.05)' : 'rgba(74,222,128,0.12)',
      color: dis ? '#2d6a4f' : '#4ade80',
      fontSize: 11,
      fontWeight: 700,
      cursor: dis ? 'not-allowed' : 'pointer',
      whiteSpace: 'nowrap',
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      flexShrink: 0,
    }),
  };

  return (
    <div style={S.wrapper}>
      {/* Cabeçalho */}
      <div style={S.header} onClick={() => setExpandido(e => !e)}>
        <div style={S.headerLeft}>
          <ClipboardList size={13} />
          Tarefas Pendentes
          {tarefas.length > 0 && <span style={S.badge}>{tarefas.length}</span>}
        </div>
        <span style={{ color: '#475569', fontSize: 12 }}>{expandido ? '▲' : '▼'}</span>
      </div>

      {expandido && (
        <div style={S.body}>
          {loading && (
            <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '8px 0' }}>Carregando...</div>
          )}

          {!loading && tarefas.length === 0 && (
            <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '8px 0' }}>
              Nenhuma tarefa pendente para este cliente.
            </div>
          )}

          {tarefas.map(t => (
            <div key={t.id} style={S.item}>
              <div style={S.itemLeft}>
                <div style={S.titulo}>{t.titulo}</div>
                <div style={S.meta}>
                  <span>{fmtDT(t.data_hora)}</span>
                  {t.usuario_nome && <span>· {t.usuario_nome}</span>}
                </div>
              </div>
              <button
                style={S.btnConcluir(marcando === t.id)}
                onClick={() => marcarConcluida(t.id)}
                disabled={marcando === t.id}
              >
                <CheckCircle2 size={12} />
                {marcando === t.id ? 'Salvando...' : 'Concluída'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
